import { useEffect, useState } from "react";
import {
  Switch,
  Form,
  Flex,
  Button,
  Col,
  Row,
  TreeSelect,
  Radio,
  Tooltip,
  Spin,
} from "antd";
import { Input, Select } from "antd";

import { useQuery } from "@tanstack/react-query";
import { listServicesCategories } from "../../../network/service";
import { queryKeys } from "../../../services/react-query/queryKeys";
import GenderSwitch from "../../common/GenderSwitch";
import { FormattedMessage, useIntl } from "react-intl";
import { checkStringInLanguage } from "../../../utils/utils";
import { CiCircleInfo } from "react-icons/ci";

const { TreeNode } = TreeSelect;

const generateServiceTreeNodes = (services: any[]) => {
  return services?.map((service) => (
    <TreeNode
      value={service?.id}
      title={service?.name}
      key={service?.id}
      style={{ color: "gray" }}
    />
  ));
};

const generateCategoryNode = (category: {
  id: any;
  title: any;
  services: any[];
}) => (
  <TreeNode
    value={`category_${category.id}`}
    title={category.title}
    style={{ color: "#00086D" }}
    disabled
    disabledStyle={{ color: "red" }}
  >
    {generateServiceTreeNodes(category.services)}
  </TreeNode>
);

const generateTreeData = (data: any[]) => {
  return data?.map((item: any) => {
    if (item.services && item.services.length > 0) {
      return generateCategoryNode(item);
    }
    return (
      <TreeNode
        value={`category_${item.id}`}
        title={item?.name}
        key={item.id}
        disabled
      >
        {generateServiceTreeNodes(item?.children)}
      </TreeNode>
    );
  });
};

interface InfoProps {
  onNextStep: () => void;
  onClose: () => void;
  serviceInfo: any;
  setServiceInfo: React.Dispatch<React.SetStateAction<{}>>;
}

function Info({ onNextStep, onClose, serviceInfo, setServiceInfo }: InfoProps) {
  const [selectedOption, setSelectedOption] = useState("both");
  const [form] = Form.useForm();

  const onFinish = (value: object) => {
    const formData = { ...value };
    onNextStep();
  };

  useEffect(() => {
    if (serviceInfo?.available_for) {
      setSelectedOption(serviceInfo?.available_for);
      form.setFieldsValue({
        available_for: serviceInfo?.available_for,
      });
    }
  }, [serviceInfo?.id]);

  useEffect(() => {
    if (serviceInfo?.available_for) {
      setSelectedOption(serviceInfo?.available_for);
      setActive(serviceInfo?.available_for);
      form.setFieldsValue({
        available_for: serviceInfo?.available_for,
      });
    } else {
      if (serviceInfo?.id) {
        setSelectedOption(serviceInfo?.available_for);
        setActive(serviceInfo?.available_for);
        const serviceDataObj = {
          name: serviceInfo?.name,
          treatment_type_id: serviceInfo?.treatment_type_id,
          // category_id: serviceInfo?.category_id,
          aftercare_description: serviceInfo?.aftercare_description,
          general_description: serviceInfo?.general_description,
          is_available: serviceInfo?.is_available,
          available_for: serviceInfo?.available_for,
          enable_online_booking: serviceInfo?.enable_online_booking,
          auto_add_to_new_sale: serviceInfo?.auto_add_to_new_sale,
        };
        console.log(serviceDataObj);
        form.setFieldsValue(serviceDataObj);
        setServiceInfo(serviceDataObj);

        setServiceInfo({
          ...serviceInfo,
          enable_team_member_commission:
            serviceInfo?.enable_team_member_commission,
          teamMember_id: serviceInfo?.team_members?.map((x: any) => x?.id),
        });
      }
    }
  }, []);

  const { data: lisstParentwithServices, isLoading: isLoadingServiceTypes } =
    useQuery([queryKeys.listParentwithServices], () =>
      listServicesCategories({
        is_parent: 1,
        with_parent: 1,
        with_children: 1,
      })
    );

  const [value, setValue] = useState<string | undefined>();

  const filterTreeNode = (input: string, treeNode: any) => {
    return treeNode.title.toLowerCase().includes(input.toLowerCase());
  };

  const onChange = (newValue: string) => {
    setValue(newValue);
  };
  const [active, setActive] = useState();
  const changeGender = (e: any) => {
    setActive(e.target.value);
  };

  const intl = useIntl();

  if (serviceInfo?.id && isLoadingServiceTypes) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spin size="large" />
      </div>
    );
  }
  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      onValuesChange={(changedValues: any, values: any) => {
        setServiceInfo({
          ...serviceInfo,
          ...changedValues,
          name: values?.name,
        });
      }}
      initialValues={{
        name: { ar: serviceInfo?.name?.ar, en: serviceInfo?.name?.en },
        service_category_id: serviceInfo?.service_category_id,
        aftercare_description: serviceInfo?.aftercare_description,
        general_description: serviceInfo?.general_description,
        location_id: serviceInfo?.location_id,
        is_available: serviceInfo?.is_available,
        auto_add_to_new_sale: serviceInfo?.auto_add_to_new_sale,
        enable_online_booking: serviceInfo?.enable_online_booking || true,
        available_for_home: serviceInfo?.available_for_home || false,
      }}
    >
      <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
        <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
          <Form.Item
            name={["name", "ar"]}
            label={
              intl.formatMessage({ id: "service_name_label" }) +
              " " +
              intl.formatMessage({ id: "inArabic" })
            }
            required={false}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "service_name_required_message",
                }),
              },
              {
                type: "string",
                min: 2,
                message: intl.formatMessage({
                  id: "service_name_length_message",
                }),
              },
              {
                validator: (_, value) => {
                  if (value) {
                    if (checkStringInLanguage(value, "ar")) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: "service_name_arabic_error",
                          })
                        )
                      );
                    }
                  }
                },
              },
            ]}
          >
            <Input
              placeholder={
                intl.formatMessage({
                  id: "service_name_placeHolder",
                }) +
                " " +
                intl.formatMessage({ id: "inArabic" })
              }
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
          <Form.Item
            name={["name", "en"]}
            label={
              intl.formatMessage({ id: "service_name_label" }) +
              " " +
              intl.formatMessage({ id: "inEnglish" })
            }
            required={false}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "service_name_required_message",
                }),
              },
              {
                type: "string",
                min: 2,
                message: intl.formatMessage({
                  id: "service_name_length_message",
                }),
              },
              {
                validator: (_, value) => {
                  if (value) {
                    if (checkStringInLanguage(value, "en")) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: "service_name_english_error",
                          })
                        )
                      );
                    }
                  }
                },
              },
            ]}
          >
            <Input
              placeholder={
                intl.formatMessage({
                  id: "service_name_placeHolder",
                }) +
                " " +
                intl.formatMessage({ id: "inEnglish" })
              }
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
          <Form.Item
            name="service_category_id"
            label={intl.formatMessage({ id: "service_type_label" })}
            required={false}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "service_type_required_message",
                }),
              },
            ]}
          >
            <TreeSelect
              showSearch
              style={{ width: "100%" }}
              placeholder={intl.formatMessage({
                id: "select_service_type_placeholder",
              })}
              dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
              allowClear
              filterTreeNode={filterTreeNode}
              value={value}
              onChange={onChange}
            >
              {generateTreeData(lisstParentwithServices?.data)}
            </TreeSelect>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 0 }} justify={"space-between"}>
        <Col className="gutter-row">
          <label className="text-base   w-full">
            {intl.formatMessage({ id: "service_availability_label" })}
          </label>
        </Col>
        <Col className="gutter-row">
          <Form.Item
            name="is_available"
            valuePropName="checked"
            className=" h-[34px]"
            // rules={[
            //     { required: true, message: "توفر الخدمة مطلوب" }
            // ]}
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} justify={"space-between"}>
        <Col className="gutter-row">
          <label className="text-base w-full">
            {intl.formatMessage({ id: "auto_add_to_new_sale_label" })}
          </label>
        </Col>
        <Col className="gutter-row">
          <Form.Item
            name="auto_add_to_new_sale"
            valuePropName="checked"
            className="float-left"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row
        gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
        justify={"space-between"}
        style={{ marginBottom: "20px" }}
        className="items-baseline"
      >
        <Col className="gutter-row">
          <label className="text-right text-normal mt-3 w-full  ">
            {intl.formatMessage({ id: "available_for" })}
          </label>
        </Col>
        <Form.Item
          name="available_for"
          required={false}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: "select_gender_message" }),
            },
          ]}
        >
          <Col className="gutter-row">
            <Radio.Group onChange={changeGender}>
              <Row
                gutter={{ xs: 8, sm: 0, md: 24, lg: 14 }}
                justify={"space-between"}
              >
                <Col className="gutter-row m-1">
                  <GenderSwitch
                    active={"woman" === active}
                    gender="woman"
                    string={intl.formatMessage({ id: "woman" })}
                  />
                </Col>
                <Col className="gutter-row m-1">
                  <GenderSwitch
                    active={"man" === active}
                    gender="man"
                    string={intl.formatMessage({ id: "man" })}
                  />
                </Col>
                <Col className="gutter-row m-1">
                  <GenderSwitch
                    active={"both" === active}
                    gender="both"
                    string={<FormattedMessage id="both_genders" />}
                  />
                </Col>
              </Row>
            </Radio.Group>
          </Col>
        </Form.Item>
      </Row>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 0 }} justify={"space-between"}>
        <Col className="gutter-row">
          <label className="text-base font-semibold w-full text-color-primary flex items-center gap-1">
            {intl.formatMessage({ id: "serviceAvailableOnHome" })}
            <Tooltip
              title={intl.formatMessage({ id: "serviceAvailableOnHomeHint" })}
            >
              <CiCircleInfo size={18} color="#CF953D" />
            </Tooltip>
          </label>
        </Col>
        <Col className="gutter-row">
          <Form.Item
            name="available_for_home"
            valuePropName="checked"
            className=" h-[34px]"
            // rules={[
            //     { required: true, message: "توفر الخدمة مطلوب" }
            // ]}
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 14 }}>
        <Col className="gutter-row" xl={12} lg={12} xs={24} sm={24}>
          <Form.Item
            name={["general_description", "ar"]}
            label={
              <div>
                {intl.formatMessage({ id: "inventory_description_label" })}
                {/* <span className="text-[10px] font-semibold mx-1 text-[#75757E]">
                  ( {intl.formatMessage({ id: "optional_label" })} )
                </span> */}
              </div>
            }
            className="w-full"
          >
            <Input.TextArea
              id="outlined-basic"
              rows={3}
              size="large"
              placeholder={intl.formatMessage({
                id: "enter_service_description_placeholder",
              })}
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" xl={12} lg={12} xs={24} sm={24}>
          <Form.Item
            name={["aftercare_description", "ar"]}
            label={
              <div>
                {intl.formatMessage({ id: "aftercare_description_label" })}
                <span className="text-[10px] font-semibold mx-1 text-[#75757E]">
                  {/* ( {intl.formatMessage({ id: "optional_label" })} ) */}
                </span>
              </div>
            }
            className="w-full"
          >
            <Input.TextArea
              id="outlined-basic"
              rows={3}
              size="large"
              placeholder={intl.formatMessage({
                id: "care_description_label",
              })}
            />
          </Form.Item>
        </Col>
      </Row>

      <Flex gap="small" align="center" justify="center">
        <Button type="primary" block htmlType="submit">
          <FormattedMessage id="continue_button" />
        </Button>
        <Button
          type="primary"
          onClick={onClose}
          block
          className="btn-primary-light"
          htmlType="button"
        >
          <FormattedMessage id="cancel_button" />
        </Button>
      </Flex>
    </Form>
  );
}

export default Info;
